import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Swal from "sweetalert2";

/**
 * @hook useExamFocusGuard
 * @description
 * Monitorea si el usuario cambia de pestaña o ventana durante un examen.
 * Muestra una alerta con cuenta regresiva. Si el usuario no regresa o acumula 3 violaciones, se ejecuta onAutoClose.
 *
 * @param {Function} onViolation - Se ejecuta si regresa antes de que acabe la cuenta regresiva.
 * @param {Function} onAutoClose - Se ejecuta si no regresa a tiempo o llega al límite de infracciones.
 * @param {number} step - Paso actual del examen.
 * @param {string} certificationType - Tipo de certificación ("epst" o "emet").
 *
 * @returns {{ violationCount: number, resetGuard: Function }}
 */
const useExamFocusGuard = (onViolation, onAutoClose, step, certificationType) => {
	const [violationCount, setViolationCount] = useState(0);
	const countdownRef = useRef(null);
	const isCountingRef = useRef(false);
	const countdown = useRef(10);

	
	// Determina si el hook debe activarse según el tipo de examen y step actual
	const shouldMonitorStep = useMemo(() => {
		const ignoredStepsByType = {
			1: [1, 3, 5, 7, 9, 11],
			2: [1, 3, 5, 7, 9]
		};

		const ignoredSteps = ignoredStepsByType[certificationType] || [];

		return !ignoredSteps.includes(step);
	}, [step, certificationType]);

	const resetGuard = useCallback(() => {
		setViolationCount(0);
		isCountingRef.current = false;
		clearInterval(countdownRef.current);
		Swal.close();
	}, []);

	const startCountdown = useCallback(() => {
		if (isCountingRef.current) return;

		isCountingRef.current = true;
		countdown.current = 10;

		Swal.fire({
			title: "¡Atención!",
			html: `Vuelve al examen en <strong><span id="countdown">10</span></strong> segundos o se cerrará.`,
			icon: "warning",
			allowOutsideClick: false,
			showConfirmButton: false,
			showCancelButton: false,
			didOpen: () => {
				const countdownEl = Swal.getHtmlContainer().querySelector("#countdown");
				countdownRef.current = setInterval(() => {
					countdown.current -= 1;
					if (countdownEl) countdownEl.textContent = countdown.current.toString();

					if (countdown.current === 0) {
						clearInterval(countdownRef.current);
						isCountingRef.current = false;
						Swal.close();
						onAutoClose();
					}
				}, 1000);
			},
			willClose: () => {
				clearInterval(countdownRef.current);
				isCountingRef.current = false;
			}
		});
	}, [onAutoClose]);

	const handleReturnToExam = useCallback(() => {
		if (!isCountingRef.current) return;

		clearInterval(countdownRef.current);
		isCountingRef.current = false;
		Swal.close();

		setViolationCount((prev) => {
			const newCount = prev + 1;
			if (newCount >= 3) {
				onAutoClose();
			} else {
				onViolation(newCount);
			}
			return newCount;
		});
	}, [onViolation, onAutoClose]);

	const handleVisibilityChange = useCallback(() => {
		if (!shouldMonitorStep) return;
		if (document.hidden) {
			startCountdown();
		} else {
			handleReturnToExam();
		}
	}, [shouldMonitorStep, startCountdown, handleReturnToExam]);

	const handleBlur = useCallback(() => {
		if (!shouldMonitorStep) return;
		startCountdown();
	}, [shouldMonitorStep, startCountdown]);

	const handleFocus = useCallback(() => {
		if (!shouldMonitorStep) return;
		handleReturnToExam();
	}, [shouldMonitorStep, handleReturnToExam]);

	useEffect(() => {
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("blur", handleBlur);
		window.addEventListener("focus", handleFocus);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("blur", handleBlur);
			window.removeEventListener("focus", handleFocus);
			clearInterval(countdownRef.current);
		};
	}, [handleVisibilityChange, handleBlur, handleFocus]);

	return { violationCount, resetGuard };
};

export default useExamFocusGuard;